import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
    fetchUserData,
    loginUser,
    refreshAccessToken,
    clearUserState,
    syncAccessToken,
    logout,
    registerUser,
    resetPassword,
    confirmUsername,
    requestResetLink,
    changePassword,
    loginUserFromToken,
    updateUser
} from '../actions/user.actions';
import UserState from "../states/user.state";
import { flagSpa } from '../actions/spa.actions';


const initialState: UserState = {
    data: null,
    loading: false,
    error: null,
    isLoggedIn: !!localStorage.getItem('accessToken'),
    accessToken: localStorage.getItem('accessToken') || null,
    expirationTime: localStorage.getItem('expirationTime')
        ? parseInt(localStorage.getItem('expirationTime')!, 10)
        : null,
    refreshing: false,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        decrementUnreadNotifications: (state) => {
            if (state.data && state.data.unread_notification_count > 0) {
                state.data.unread_notification_count -= 1;
            }
        },
        // Forms that show state.user.error call this when the user moves on, so an
        // error from one step doesn't linger on the next.
        clearUserError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {

        builder
            // Fetch user data
            .addCase(fetchUserData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserData.fulfilled, (state, action: PayloadAction<any>) => {
                state.loading = false;
                // Logged out while the request was in flight (e.g. from another tab). Keeping the
                // user without a token sends AuthRoute and ProtectedRoute into a redirect loop.
                if (!state.accessToken) return;
                state.data = action.payload;
                state.error = null;
            })
            .addCase(fetchUserData.rejected, (state, action) => {
                state.loading = false;
            })

            // Login:
            .addCase(loginUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.loading = false;
                state.isLoggedIn = true;
                state.data = action.payload.user;
                state.accessToken = action.payload.accessToken;
                state.expirationTime = Math.floor(action.payload.expirationTime);
                state.error = null;
                localStorage.setItem('accessToken', action.payload.accessToken);
                localStorage.setItem('expirationTime', Math.floor(action.payload.expirationTime).toString());
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Refresh token:
            .addCase(refreshAccessToken.pending, (state) => {
                state.refreshing = true;
            })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                state.refreshing = false;
                state.accessToken = action.payload.accessToken;
                state.expirationTime = Math.floor(action.payload.expirationTime);
                localStorage.setItem('accessToken', action.payload.accessToken);
                localStorage.setItem('expirationTime', Math.floor(action.payload.expirationTime).toString());
            })
            .addCase(refreshAccessToken.rejected, (state) => {
                state.refreshing = false;
            })

            // Token from another tab (already in localStorage):
            .addCase(syncAccessToken, (state, action) => {
                if (state.accessToken === action.payload.accessToken) return;
                state.accessToken = action.payload.accessToken;
                state.expirationTime = action.payload.expirationTime;
                state.isLoggedIn = true;
            })

            // Logout:
            .addCase(logout.fulfilled, (state) => {
                state.accessToken = null;
                state.isLoggedIn = false;
                state.data = null;
                state.error = null;
                state.expirationTime = null;
                localStorage.removeItem('accessToken');
                localStorage.removeItem('expirationTime');
            })

            // Clear user state:
            .addCase(clearUserState, (state) => {
                state.accessToken = null;
                state.isLoggedIn = false;
                state.data = null;
                state.error = null;
                state.expirationTime = null;
                localStorage.removeItem('accessToken');
                localStorage.removeItem('expirationTime');
            })

            // Register:
            .addCase(registerUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(registerUser.fulfilled, (state, action) => {
                state.loading = false;
                state.isLoggedIn = true;
                state.data = action.payload.user;
                state.accessToken = action.payload.accessToken;
                state.expirationTime = Math.floor(action.payload.expirationTime);
                state.error = null;
                localStorage.setItem('accessToken', action.payload.accessToken);
                localStorage.setItem('expirationTime', Math.floor(action.payload.expirationTime).toString());
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Reset password:
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Confirm username:
            .addCase(confirmUsername.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(confirmUsername.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(confirmUsername.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Request password link:
            .addCase(requestResetLink.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestResetLink.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(requestResetLink.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Edit Profile:
            .addCase(updateUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUser.fulfilled, (state, action) => {
                state.loading = false;
                // Only update the current user's data if they are updating themselves
                // Merge old data with new data to preserve all existing fields
                if (action.payload.isSelfUpdate && state.data) {
                    state.data = {...state.data, ...action.payload.user};
                }
                state.error = null;
            })
            .addCase(updateUser.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Change Password:
           .addCase(changePassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.loading = false;
                state.isLoggedIn = true;
                state.error = null;
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? null;
            })

            // Login with Token:
            .addCase(loginUserFromToken.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(loginUserFromToken.fulfilled, (state, action) => {
                state.loading = false;
                state.isLoggedIn = true;
                state.data = action.payload.user;
                state.accessToken = action.payload.accessToken;
                state.expirationTime = Math.floor(action.payload.expirationTime);
                state.error = null;
                localStorage.setItem('accessToken', action.payload.accessToken);
                localStorage.setItem('expirationTime', Math.floor(action.payload.expirationTime).toString());
            })
            .addCase(loginUserFromToken.rejected, (state, action) => {
                state.loading = false;
                state.error = `JWT Error: ${action.payload}`;
            })

            .addCase(flagSpa.pending, (state) => {
                state.error = null;
            })
            .addCase(flagSpa.fulfilled, (state,action: PayloadAction<any>) => {
                state.data = {...state.data, flagged_spas_count: action.payload.is_flagged ? state.data.flagged_spas_count+1 : state.data.flagged_spas_count-1};
            })
            .addCase(flagSpa.rejected, (state, action) => {
                state.error = action.payload ?? null;
            });
    },
});

export const { decrementUnreadNotifications, clearUserError } = userSlice.actions;
export default userSlice.reducer;